import { jsonify, sanitize } from "../TapiocaUtils";
export default class ScribbleClient {
  constructor(raw) {
    this.raw = raw;
  }
  completeUpload(projectUid, token) {
    const url = `/projects/${projectUid}/scribble_uploads/completions/${token}`;
    return this.raw.get(url);
  }
  /**
   * Create a new Annotation in a project.
   */
  createAnnotation(projectUid, body) {
    const url = `/projects/${projectUid}/annotations2`;
    return this.raw.post(url, jsonify(body));
  }
  createAnnotationLink(projectUid, annotationUid, body) {
    const url = `/projects/${projectUid}/annotations2/${annotationUid}/links`;
    return this.raw.post(url, jsonify(body));
  }
  createJob(projectUid, body) {
    const url = `/projects/${projectUid}/scribble_jobs`;
    return this.raw.post(url, jsonify(body));
  }
  /**
   * Create a new Layer in a project.
   */
  createLayer(projectUid, body) {
    const url = `/projects/${projectUid}/layers`;
    return this.raw.post(url, jsonify(body));
  }
  createStampImage(body) {
    const url = "/stamp_images";
    return this.raw.post(url, jsonify(body));
  }
  /**
   * Create a new Surface in a project.
   */
  createSurface(projectUid, body) {
    const url = `/projects/${projectUid}/surfaces`;
    return this.raw.post(url, jsonify(body));
  }
  createUpload(projectUid, body) {
    const url = `/projects/${projectUid}/scribble_uploads`;
    return this.raw.post(url, jsonify(body));
  }
  deleteAnnotation(projectUid, uid) {
    const url = `/projects/${projectUid}/annotations2/${uid}`;
    return this.raw.delete(url);
  }
  deleteAnnotationLink(projectUid, annotationUid, linkUid) {
    const url = `/projects/${projectUid}/annotations2/${annotationUid}/links/${linkUid}`;
    return this.raw.delete(url);
  }
  deleteAnnotationTemplate(templateUid) {
    const url = `/annotation_templates/${templateUid}`;
    return this.raw.delete(url);
  }
  /**
   * Delete a/n Layer in a project.
   */
  deleteLayer(projectUid, uid) {
    const url = `/projects/${projectUid}/layers/${uid}`;
    return this.raw.delete(url);
  }
  deleteStampImage(stampImageUid) {
    const url = `/stamp_images/${stampImageUid}`;
    return this.raw.delete(url);
  }
  /**
   * Delete a/n Surface in a project.
   */
  deleteSurface(projectUid, uid) {
    const url = `/projects/${projectUid}/surfaces/${uid}`;
    return this.raw.delete(url);
  }
  /**
   * Retrieve one Annotation in a project.
   */
  getAnnotation(projectUid, uid) {
    const url = `/projects/${projectUid}/annotations2/${uid}`;
    return this.raw.get(url);
  }
  getAnnotationTemplates(params, options = { getAll: false }) {
    const url = "/annotation_templates";
    if (options.getAll) {
      return this.raw.getAll(url, sanitize(params || {}, {}));
    }
    return this.raw.get({ url, params: sanitize(params || {}, {}) });
  }
  /**
   * Return the history of a given surface.
   * Returns an array of surface UIDs, including <surface_uid>.
   */
  getHistory(projectUid, surfaceUid) {
    const url = `/projects/${projectUid}/surfaces/${surfaceUid}/history`;
    return this.raw.get(url);
  }
  getJob(projectUid, jobUid, params) {
    const url = {
      url: `/projects/${projectUid}/scribble_jobs/${jobUid}`,
      params: sanitize(params || {}),
    };
    return this.raw.get(url);
  }
  /**
   * Retrieve one Layer in a project.
   */
  getLayer(projectUid, uid) {
    const url = `/projects/${projectUid}/layers/${uid}`;
    return this.raw.get(url);
  }
  getLayerAnnotations(
    projectUid,
    layerUid,
    params,
    options = { getAll: false }
  ) {
    const url = `/projects/${projectUid}/layers/${layerUid}/annotations2`;
    if (options.getAll) {
      return this.raw.getAll(url, sanitize(params || {}, {}));
    }
    return this.raw.get({ url, params: sanitize(params || {}, {}) });
  }
  getStampImages(params, options = { getAll: false }) {
    const url = "/stamp_images";
    if (options.getAll) {
      return this.raw.getAll(url, sanitize(params || {}, {}));
    }
    return this.raw.get({ url, params: sanitize(params || {}, {}) });
  }
  /**
   * Retrieve one Surface in a project.
   */
  getSurface(projectUid, uid) {
    const url = `/projects/${projectUid}/surfaces/${uid}`;
    return this.raw.get(url);
  }
  getSurfaceAnnotations(
    projectUid,
    surfaceUid,
    params,
    options = { getAll: false }
  ) {
    const url = `/projects/${projectUid}/surfaces/${surfaceUid}/annotations2`;
    if (options.getAll) {
      return this.raw.getAll(url, sanitize(params || {}, {}));
    }
    return this.raw.get({ url, params: sanitize(params || {}, {}) });
  }
  getSurfaceLayers(
    projectUid,
    surfaceUid,
    params,
    options = { getAll: false }
  ) {
    const url = `/projects/${projectUid}/surfaces/${surfaceUid}/layers`;
    if (options.getAll) {
      return this.raw.getAll(url, sanitize(params || {}, {}));
    }
    return this.raw.get({ url, params: sanitize(params || {}, {}) });
  }
  /**
   * Sync annotations for a project.
   */
  syncAnnotations(projectUid, params) {
    const url = {
      url: `/projects/${projectUid}/annotations2_/sync`,
      params: sanitize(params || {}),
    };
    return this.raw.get(url);
  }
  /**
   * Sync layers for a project.
   */
  syncLayers(projectUid, params) {
    const url = {
      url: `/projects/${projectUid}/layers_/sync`,
      params: sanitize(params || {}),
    };
    return this.raw.get(url);
  }
  /**
   * Sync surfaces for a project.
   */
  syncSurfaces(projectUid, params) {
    const url = {
      url: `/projects/${projectUid}/surfaces_/sync`,
      params: sanitize(params || {}),
    };
    return this.raw.get(url);
  }
  /**
   * Update a/n Annotation in a project.
   * This will perform an "upsert" if the Annotation with the
   * uid does not exist.
   */
  updateAnnotation(projectUid, uid, body) {
    const url = `/projects/${projectUid}/annotations2/${uid}`;
    return this.raw.put(url, jsonify(body));
  }
  /**
   * Update a/n Layer in a project.
   * This will perform an "upsert" if the Layer with the
   * uid does not exist.
   */
  updateLayer(projectUid, uid, body) {
    const url = `/projects/${projectUid}/layers/${uid}`;
    return this.raw.put(url, jsonify(body));
  }
  /**
   * Update a/n Surface in a project.
   * This will perform an "upsert" if the Surface with the
   * uid does not exist.
   */
  updateSurface(projectUid, uid, body) {
    const url = `/projects/${projectUid}/surfaces/${uid}`;
    return this.raw.put(url, jsonify(body));
  }
  upsertAnnotationTemplate(templateUid, body) {
    const url = `/annotation_templates/${templateUid}`;
    return this.raw.put(url, jsonify(body));
  }
}
